import { JulianDate, Cartesian3, Quaternion, SampledPositionProperty, SampledProperty, HeadingPitchRoll, HermitePolynomialApproximation, Matrix3, CallbackProperty, ReferenceFrame, Transforms, VelocityVectorProperty, defined, Cartographic, LagrangePolynomialApproximation, } from "cesium";
import { eciToGeodetic, gstime, propagate, twoline2satrec } from "satellite.js";
import { REFERENCE_FRAME, UNITS_DEF, } from "../../Entities/types";
import { getParsedValue, km2meters, degToRad } from "./converters";
import { meanMotionFromSMA, orb2sv, sv2orb } from "./propagation";
import { getJulianDateFromEpoch, mjd2unix } from "./simulationTime";
export * from "./simulationTime";
export * from "./attitudes";
export * from "./converters";
export * from "./usePreloadIcrfFixed";
export * from "./swath";
export * from "./rotationUtils";
export const wildcard2RegExp = (wildcardExp) => {
    const regExpString = wildcardExp
        .split(/\./)
        .join("\\.")
        .split(/\*+/)
        .join(".*")
        .split(/\+/)
        .join("\\+");
    return new RegExp("^" + regExpString + "$");
};
export const isSrcUrl = (src) => {
    return (/^(?:\/|[a-z]+:\/\/)/.test(src) ||
        /^(?:\/|[a-z]+:\/\/)/.test(src.substring(1)));
};
export const isJsonString = (str) => {
    try {
        JSON.parse(str);
    }
    catch (e) {
        return false;
    }
    return true;
};
export const canDisplayObject = (object) => {
    return (!!object &&
        !!object.x &&
        !!object.y &&
        !!object.z &&
        !!object.xDot &&
        !!object.yDot &&
        !!object.zDot);
};
export const getSampledPropertyCallback = (property) => {
    return new CallbackProperty((time) => property.getValue(time), false);
};
const getTracePointFromCartesian3 = (cartesian) => {
    let cartographicTracePoint = Cartographic.fromCartesian(cartesian);
    cartographicTracePoint.height = 0;
    return Cartographic.toCartesian(cartographicTracePoint);
};
export const getOrbitAndTraceSamplesFromPositionMap = (svectors, refFrame) => {
    const orbitPositions = new SampledPositionProperty(ReferenceFrame.INERTIAL);
    const tracePositions = new SampledPositionProperty(ReferenceFrame.FIXED);
    svectors.forEach((sv) => {
        const time = getJulianDateFromEpoch(sv.epoch);
        const position = new Cartesian3(getParsedValue(sv.x, UNITS_DEF.KM), getParsedValue(sv.y, UNITS_DEF.KM), getParsedValue(sv.z, UNITS_DEF.KM));
        if (refFrame === REFERENCE_FRAME.EARTH_FIXED) {
            const toIcrf = Transforms.computeFixedToIcrfMatrix(time, new Matrix3());
            Matrix3.multiplyByVector(toIcrf, position, position);
        }
        const posGeodetic = Cartographic.fromCartesian(position);
        const cart = Cartesian3.fromRadians(posGeodetic.longitude, posGeodetic.latitude, 0);
        const toFixed = Transforms.computeIcrfToFixedMatrix(time, new Matrix3());
        if (!defined(toFixed)) {
            return undefined;
        }
        Matrix3.multiplyByVector(toFixed, cart, cart);
        orbitPositions.addSample(time, position);
        tracePositions.addSample(time, cart);
    });
    orbitPositions.setInterpolationOptions({
        interpolationDegree: 8,
        interpolationAlgorithm: LagrangePolynomialApproximation,
    });
    tracePositions.setInterpolationOptions({
        interpolationDegree: 8,
        interpolationAlgorithm: LagrangePolynomialApproximation,
    });
    return {
        orbitPositions,
        tracePositions,
    };
};
export const getOrbitAndTraceSamplesFromStateVector = (sv, tca) => {
    const { x, y, z, xDot, yDot, zDot } = sv;
    const kep = sv2orb(x, y, z, xDot, yDot, zDot);
    const { a } = kep;
    const startTime = "epoch" in sv
        ? getJulianDateFromEpoch(sv.epoch)
        : tca
            ? tca
            : JulianDate.now();
    const orbitPeriod = (2 * Math.PI) / meanMotionFromSMA(a);
    const { orbitPositions, tracePositions } = getOrbitAndTraceSamplesFromKeplerianElements(kep, startTime, orbitPeriod);
    return {
        orbitPositions,
        tracePositions,
        orbitPeriod,
    };
};
export const getOrbitAndTraceSamplesFromKeplerianElements = (keplerianElements, startTime, orbitPeriod) => {
    const orbitPositions = new SampledPositionProperty(ReferenceFrame.FIXED);
    const tracePositions = new SampledPositionProperty(ReferenceFrame.FIXED);
    const { a, e, i, ω, Ω, M } = keplerianElements;
    const orbitPoints = 1000;
    const step = orbitPeriod / orbitPoints;
    const halfPeriod = orbitPeriod / 2;
    const start = JulianDate.addSeconds(startTime, -halfPeriod, new JulianDate());
    for (let index = 0; index < orbitPoints; index++) {
        const time = JulianDate.addSeconds(start, index * step, new JulianDate());
        const pv = orb2sv(a, e, i, ω, Ω, M, halfPeriod, index * step);
        const position = new Cartesian3(pv.x, pv.y, pv.z);
        orbitPositions.addSample(time, position);
        const tracePoint = getTracePointFromCartesian3(position);
        tracePositions.addSample(time, tracePoint);
    }
    orbitPositions.setInterpolationOptions({
        interpolationDegree: 5,
        interpolationAlgorithm: LagrangePolynomialApproximation,
    });
    tracePositions.setInterpolationOptions({
        interpolationDegree: 5,
        interpolationAlgorithm: LagrangePolynomialApproximation,
    });
    return {
        orbitPositions,
        tracePositions,
    };
};
export const getOrbitAndTraceSamplesFromTLE = (tle, startTime, stopTime, orbitPeriod) => {
    const orbitPositions = new SampledPositionProperty(ReferenceFrame.FIXED);
    const tracePositions = new SampledPositionProperty(ReferenceFrame.FIXED);
    const satrec = twoline2satrec(tle.line1, tle.line2);
    if (satrec.error) {
        console.log(satrec.error);
        return { orbitPositions, tracePositions };
    }
    const secondsDiff = JulianDate.secondsDifference(stopTime, startTime);
    for (var dt = 0; dt < secondsDiff; dt += secondsDiff / orbitPeriod ? orbitPeriod : 3600) {
        const dateObj = JulianDate.toDate(JulianDate.addSeconds(startTime, dt, new JulianDate()));
        const positionAndVelocity = propagate(satrec, dateObj);
        const positionEci = positionAndVelocity.position;
        const gmst = gstime(dateObj);
        const { longitude, latitude, height } = eciToGeodetic(positionEci, gmst);
        const pos = Cartographic.toCartesian(Cartographic.fromRadians(longitude, latitude, km2meters(height)));
        const cart = Cartographic.toCartesian(Cartographic.fromRadians(longitude, latitude, 0));
        orbitPositions.addSample(JulianDate.fromDate(dateObj), pos);
        tracePositions.addSample(JulianDate.fromDate(dateObj), cart);
    }
    orbitPositions.setInterpolationOptions({
        interpolationDegree: 5,
        interpolationAlgorithm: LagrangePolynomialApproximation,
    });
    tracePositions.setInterpolationOptions({
        interpolationDegree: 5,
        interpolationAlgorithm: LagrangePolynomialApproximation,
    });
    return {
        orbitPositions,
        tracePositions,
    };
};
export const getAttitudeSampleFromAttitudeMap = (vectors) => {
    if (!vectors) {
        return undefined;
    }
    const sampledAttitude = new SampledProperty(Quaternion);
    vectors === null || vectors === void 0 ? void 0 : vectors.forEach((stateVector, index) => {
        const julianDate = typeof stateVector.epoch === "string"
            ? JulianDate.fromIso8601(stateVector.epoch, new JulianDate())
            : JulianDate.fromDate(new Date(mjd2unix(stateVector.epoch)));
        const thisSampleAttitude = Quaternion.fromHeadingPitchRoll(new HeadingPitchRoll(stateVector.z[index], stateVector.y[index], stateVector.x[index]), new Quaternion());
        sampledAttitude.addSample(julianDate, thisSampleAttitude);
    });
    sampledAttitude.setInterpolationOptions({
        interpolationDegree: 8,
        interpolationAlgorithm: HermitePolynomialApproximation,
    });
    return sampledAttitude;
};
export const getChildPosition = (parentPosition, parentOrientation, positionRelativeToParent) => {
    const orientationMatrix = Matrix3.fromQuaternion(parentOrientation, new Matrix3());
    const relativePosition = Matrix3.multiplyByVector(orientationMatrix, positionRelativeToParent, new Cartesian3());
    return Cartesian3.add(parentPosition, relativePosition, new Cartesian3());
};
export const getFinalChildPosition = (parentPosition, parentOrientation, positionToParent, time) => {
    if (!positionToParent || positionToParent.equals(new Cartesian3(0, 0, 0))) {
        return parentPosition.getValue(time);
    }
    const thisSampleParentPosition = parentPosition.getValue(time);
    const thisSampleParentOrientation = parentOrientation.getValue(time);
    const orientationMatrix = Matrix3.fromQuaternion(thisSampleParentOrientation, new Matrix3());
    const thisSampleSensorPositionToSatellite = Matrix3.multiplyByVector(orientationMatrix, positionToParent, new Cartesian3());
    const thisSampleSensorPosition = Cartesian3.add(thisSampleParentPosition, thisSampleSensorPositionToSatellite, new Cartesian3());
    return thisSampleSensorPosition;
};
export const getChildAttitude = (attitude, parentOrientation, time) => {
    if (!parentOrientation) {
        return defined(attitude) ? attitude.getValue(time) : Quaternion.IDENTITY;
    }
    if (!attitude) {
        return parentOrientation.getValue(time);
    }
    const thisSampleSensorOrientation = attitude.getValue(time);
    const thisSampleParentOrientation = parentOrientation.getValue(time);
    const combinedOrientation = Quaternion.multiply(thisSampleParentOrientation, thisSampleSensorOrientation, new Quaternion());
    return combinedOrientation;
};
export const getCylinderOrientationFrom2Points = (source, destination) => {
    let directionVector = Cartesian3.subtract(destination, source, new Cartesian3());
    Cartesian3.normalize(directionVector, directionVector);
    let rotationMatrix = Transforms.rotationMatrixFromPositionVelocity(source, directionVector);
    let rot90 = Matrix3.fromRotationY(degToRad(90));
    Matrix3.multiply(rotationMatrix, rot90, rotationMatrix);
    return Quaternion.fromRotationMatrix(rotationMatrix, new Quaternion());
};
export const getIntersection3D = (origin1, destination1, origin2, destination2) => {
    const firstSlope = (destination1.z - origin1.z) / (destination1.x - origin1.x);
    const firstIntercept = origin1.z - firstSlope * origin1.x;
    const secondSlope = (destination2.z - origin2.z) / (destination2.x - origin2.x);
    const secondIntercept = origin2.z - secondSlope * origin2.x;
    const intersectionX = (secondIntercept - firstIntercept) / (firstSlope - secondSlope);
    const intersectionZ = firstSlope * intersectionX + firstIntercept;
    const centerOrigin = Cartesian3.midpoint(origin1, origin2, new Cartesian3());
    const centerDestination = Cartesian3.midpoint(destination1, destination2, new Cartesian3());
    const directionVector = Cartesian3.normalize(Cartesian3.subtract(centerDestination, centerOrigin, new Cartesian3()), new Cartesian3());
    const alpha = (intersectionX - centerOrigin.x) / directionVector.x;
    const intersectionY = centerOrigin.y + alpha * directionVector.y;
    return new Cartesian3(intersectionX, intersectionY, intersectionZ);
};
export function unpackSampledPositionProperty(sampledPositions, from, to, numberOfValues) {
    const unpackedPositions = { times: [], positions: [], velocities: [] };
    const velocities = new VelocityVectorProperty(sampledPositions, false);
    const secondsDifference = JulianDate.secondsDifference(to, from);
    for (var t = 0; t <= secondsDifference; t += secondsDifference / numberOfValues) {
        const time = JulianDate.addSeconds(from, t, new JulianDate());
        const position = sampledPositions.getValueInReferenceFrame(time, ReferenceFrame.FIXED);
        const velocity = velocities.getValue(time, new Cartesian3());
        if (time && position && velocity) {
            unpackedPositions.times.push(JulianDate.toIso8601(time, 3));
            unpackedPositions.positions.push(position);
            unpackedPositions.velocities.push(velocity);
        }
    }
    return unpackedPositions;
}
export function unpackPositions(callbackProperty, from, to, numberOfValues) {
    const unpackedPositions = { times: [], positions: [] };
    const secondsDifference = JulianDate.secondsDifference(to, from);
    for (var t = 0; t <= secondsDifference; t += secondsDifference / numberOfValues) {
        const time = JulianDate.addSeconds(from, t, new JulianDate());
        const position = callbackProperty.getValue(time);
        if (time && position) {
            unpackedPositions.times.push(JulianDate.toIso8601(time));
            unpackedPositions.positions.push(position);
        }
    }
    return unpackedPositions;
}
export function unpackAttitudes(sampledProperty, from, to, numberOfValues) {
    const unpackedValues = { times: [], orientations: [] };
    const secondsDifference = JulianDate.secondsDifference(to, from);
    for (var t = 0; t <= secondsDifference; t += secondsDifference / numberOfValues) {
        const time = JulianDate.addSeconds(from, t, new JulianDate());
        const orientation = sampledProperty.getValue(time);
        if (time && orientation) {
            unpackedValues.times.push(JulianDate.toIso8601(time, 3));
            unpackedValues.orientations.push(orientation);
        }
    }
    return unpackedValues;
}
export function vfColour2RGB(vfColor) {
    var color;
    switch (vfColor) {
        case "WHITE":
            color = { R: 255, G: 255, B: 255, A: 255 };
            break;
        case "RED":
            color = { R: 255, G: 0, B: 0, A: 255 };
            break;
        case "GREEN":
            color = { R: 0, G: 255, B: 0, A: 255 };
            break;
        case "BLUE":
            color = { R: 0, G: 0, B: 255, A: 255 };
            break;
        case "YELLOW":
            color = { R: 255, G: 255, B: 0, A: 255 };
            break;
        case "ORANGE":
            color = { R: 255, G: 165, B: 0, A: 255 };
            break;
        case "MAGENTA":
            color = { R: 255, G: 0, B: 255, A: 255 };
            break;
        case "PURPLE":
            color = { R: 128, G: 0, B: 128, A: 255 };
            break;
        case "CYAN":
            color = { R: 0, G: 255, B: 255, A: 255 };
            break;
        case "DARK_GREEN":
            color = { R: 0, G: 100, B: 0, A: 255 };
            break;
        case "BROWN":
            color = { R: 135, G: 58, B: 25, A: 255 };
            break;
        case "LIGHT_GRAY":
            color = { R: 211, G: 211, B: 211, A: 255 };
            break;
        case "MED_GRAY":
            color = { R: 169, G: 169, B: 169, A: 255 };
            break;
        case "DARK_GRAY":
            color = { R: 105, G: 105, B: 105, A: 255 };
            break;
        case "BLACK":
            color = { R: 0, G: 0, B: 0, A: 255 };
            break;
        case "NOCOLOR":
            color = { R: 0, G: 0, B: 0, A: 0 };
            break;
        default:
            color = { R: 255, G: 255, B: 255, A: 255 };
            break;
    }
    return color;
}
export const thrusterRotation = Quaternion.fromHeadingPitchRoll(HeadingPitchRoll.fromDegrees(-90.0, 0.0, 0.0));
export const computeErrorEllipsoid = (covarianceMatrix, position, velocity) => {
    const cov = covarianceMatrix;
    let covMatrix;
    if ("crR" in cov) {
        covMatrix = [
            [
                getParsedValue(cov.crR, UNITS_DEF.KM2),
                getParsedValue(cov.ctR, UNITS_DEF.KM2),
                getParsedValue(cov.cnR, UNITS_DEF.KM2),
            ],
            [
                getParsedValue(cov.ctR, UNITS_DEF.KM2),
                getParsedValue(cov.ctT, UNITS_DEF.KM2),
                getParsedValue(cov.cnT, UNITS_DEF.KM2),
            ],
            [
                getParsedValue(cov.cnR, UNITS_DEF.KM2),
                getParsedValue(cov.cnT, UNITS_DEF.KM2),
                getParsedValue(cov.cnN, UNITS_DEF.KM2),
            ],
        ];
    }
    if ("cxX" in cov) {
        covMatrix = [
            [
                getParsedValue(cov.cxX, UNITS_DEF.KM2),
                getParsedValue(cov.cyX, UNITS_DEF.KM2),
                getParsedValue(cov.czX, UNITS_DEF.KM2),
            ],
            [
                getParsedValue(cov.cyX, UNITS_DEF.KM2),
                getParsedValue(cov.cyY, UNITS_DEF.KM2),
                getParsedValue(cov.czY, UNITS_DEF.KM2),
            ],
            [
                getParsedValue(cov.czX, UNITS_DEF.KM2),
                getParsedValue(cov.czY, UNITS_DEF.KM2),
                getParsedValue(cov.czZ, UNITS_DEF.KM2),
            ],
        ];
    }
    const covMatrix3 = Matrix3.fromRowMajorArray(covMatrix.flat());
    const { unitary, diagonal } = Matrix3.computeEigenDecomposition(covMatrix3);
    const radii = new Cartesian3(Math.sqrt(diagonal[0]), Math.sqrt(diagonal[4]), Math.sqrt(diagonal[8]));
    const ellipsoid2RTNQuaternion = Quaternion.fromRotationMatrix(unitary, new Quaternion());
    let interMediate = new Quaternion();
    const orientation = new CallbackProperty(function (time) {
        const rtn2Inertial = computeRTN(position, velocity, time);
        Quaternion.multiply(rtn2Inertial, ellipsoid2RTNQuaternion, interMediate);
        return interMediate;
    }, false);
    return { orientation, radii };
};
export const computeRTN = (position, velocity, time) => {
    const R = Cartesian3.normalize(position instanceof Cartesian3 ? position : position.getValue(time), new Cartesian3());
    const V = Cartesian3.normalize(velocity instanceof Cartesian3 ? velocity : velocity.getValue(time), new Cartesian3());
    const N = new Cartesian3();
    Cartesian3.cross(R, V, N);
    Cartesian3.normalize(N, N);
    const T = new Cartesian3();
    Cartesian3.cross(N, R, T);
    Cartesian3.normalize(T, T);
    const matrix = [
        Cartesian3.pack(R, []),
        Cartesian3.pack(T, []),
        Cartesian3.pack(N, []),
    ].flat();
    const RTN2Inertial = Matrix3.fromColumnMajorArray(matrix);
    return Quaternion.fromRotationMatrix(RTN2Inertial, new Quaternion());
};
//# sourceMappingURL=index.js.map